import DeliverableTransferModel               from '@/models/services/shop/DeliverableTransferModel';
import List                                   from '@/components/list/List';
import TransferIcon                           from '@/assets/icons/TransferIcon';
import TransfersListRow                       from '@/pages/Transfers/components/TransfersListRow';
import authStore                              from '@/stores/authStore';
import useApiCollection                       from '@widesk-core/hooks/useApiCollection';
import usePagedCollection                     from '@widesk-core/hooks/usePagedCollection';
import { useTranslation }                     from 'react-i18next';
import DeliverableTransferStatusModel         from '@/models/services/shop/DeliverableTransferStatusModel';
import { DeliverableTransferStatusReference } from '@/models/services/shop/DeliverableTransferStatusModel';
import { ModalData }                          from '@/components/modal/Modal';
import { fetchImageFor }                      from '@/models/fetchImageFor';
import PagedCollection                        from '@widesk-core/models/PagedCollection';

interface ITransfersListBoxProps {
	setModalData: React.Dispatch<React.SetStateAction<ModalData | undefined>>;
	setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	onListSuccess?: (collection: PagedCollection<DeliverableTransferModel>) => void;
	options?: {
		bottomLink?: {
			label: string;
			to: string;
		};
		status?: DeliverableTransferStatusReference,
		withPagination?: boolean,
	}
}

const TransfersListBox = (props: ITransfersListBoxProps) => {

	const { setModalData, setModalIsOpen, onListSuccess, options } = props;

	const { t } = useTranslation();
	const currentUserUrn = authStore.currentUser.getUrn();

	const deliverableTransferCollection = usePagedCollection(DeliverableTransferModel);
	const deliverableTransferStatusCollection = useApiCollection(DeliverableTransferStatusModel);
	const currentOwnerDeliverableTransferCollection = useApiCollection(DeliverableTransferModel);
	const newOwnerDeliverableTransferCollection = useApiCollection(DeliverableTransferModel);

	return (
		<List
			afterFetch={async () => {
				if (onListSuccess) {
					onListSuccess(deliverableTransferCollection);
				}
				await Promise.all(deliverableTransferCollection.models?.map(async (model) => {
					if (model.getId('currentOwner')) {
						return model.currentOwner.fetch();
					}
				}));
				await Promise.all([
					deliverableTransferCollection.whenIsLoaded(m => m.deliverableTransferStatus),
					deliverableTransferCollection.whenIsLoaded(m => m.productVariant.product.entertainment),
				]);
				const products = deliverableTransferCollection.models
					.filter(m => m.productVariant.product.id)
					.map(m => m.productVariant.product);
				await fetchImageFor(products, 'image', 'product_main_visual');
			}}
			beforeFetch={async () => {
				await currentOwnerDeliverableTransferCollection.listBy([currentUserUrn], 'currentOwner.userUrn');
				await newOwnerDeliverableTransferCollection.listBy([currentUserUrn], 'nextOwner.userUrn');
				await deliverableTransferStatusCollection.list();
				if (options?.status) {
					const status = deliverableTransferStatusCollection.find(m => m.reference === options.status);
					if (status?.id) {
						deliverableTransferCollection.setFilters({
							'deliverableTransferStatus': status.id,
							'id': [...currentOwnerDeliverableTransferCollection.ids, ...newOwnerDeliverableTransferCollection.ids],
						})
					}
				} else {
					deliverableTransferCollection.setRequiredFilter('id', [...currentOwnerDeliverableTransferCollection.ids, ...newOwnerDeliverableTransferCollection.ids]);
				}
			}}
			collection={deliverableTransferCollection}
			renderRow={(model) => <TransfersListRow
				deliverableTransfer={model}
				deliverableTransferCollection={deliverableTransferCollection}
				deliverableTransferStatusCollection={deliverableTransferStatusCollection}
				setModalData={setModalData}
				setModalIsOpen={setModalIsOpen}
			/>}
			options={{
				emptyMessages: {
					title: t('pages.transfers.list.empty.title'),
					description: t('pages.transfers.list.empty.description'),
					icon: <TransferIcon size="xl" />,
				},
				itemsMargin: '10px 0px 10px 0px',
				itemsPadding: '10px',
				padding: '0px 10px 0px 10px',
				withSeparators: true,
				withPagination: options?.withPagination,
				bottomLink: options?.bottomLink,
			}}
		/>
	)
}

export default TransfersListBox;